import { useContext, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import cx from 'classnames'
import { Item } from '@react-stately/collections'

import Styles from './TemplatePicker.module.scss'

import { ReactComponent as ChevronUp } from 'assets/icons/chevronUp16.svg'
import { ReactComponent as ChevronDown } from 'assets/icons/chevronDown16.svg'
import { ReactComponent as CloseIcon } from 'assets/icons/close16.svg'
import { Button } from 'components/Button'
import DocCardSlider from 'components/DocCardSlider/DocCardSlider'
import { Menu } from 'components/Menu/Menu'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { TemplateMiniDocCard } from './TemplateMiniDocCard'
import useTemplateCategories from '../CreateFromTemplateModal/hooks/useTemplateCategories'
import useTemplateChangeListener from 'hooks/templates/useTemplateChangeListener'
import templatesStore from 'stores/TemplatesStore'
import { ArticleMeta } from 'types'
import { EditorStateContext } from 'common/Prose/EditorProvider/EditorProviderContext'
import { useHandbook } from '../Handbook2_0/hooks'
import { useRootStore } from 'stores/RootStore'

interface TemplatePickerProps {
  article: ArticleMeta
  visible: boolean
}

export const TemplatePicker = observer(({ article, visible }: TemplatePickerProps) => {
  useTemplateChangeListener(article)
  const handbook = useHandbook()
  const rootStore = useRootStore()
  const editorState = useContext(EditorStateContext)
  const [category, setCategory] = useState<any>()
  const categoriesQuery = useTemplateCategories({
    enabled: visible,
    onSuccess: (data) => {
      if (!data?.length) {
        templatesStore.closePicker()
      }
    },
    onError: () => {
      templatesStore.closePicker()
    },
  })

  const categories = categoriesQuery.data
  const { selectedCategory } = templatesStore
  const ref = useRef(null)

  useEffect(() => {
    if (!categories || !visible) {
      return
    }

    if (selectedCategory) {
      return setCategory(categories.find((x) => x.name.toLowerCase() === selectedCategory.toLowerCase()))
    }

    return setCategory(categories.find((x) => x.name.toLowerCase().includes('popular')) || categories[0])
  }, [categories, selectedCategory, visible])

  const [menuOpen, setMenuOpen] = useState(false)
  const menuItems = (categories || []).map((category) => {
    return (
      <Item key={category.name} textValue={category.name}>
        {category.name}
      </Item>
    )
  })

  if (!visible || categoriesQuery.isLoading) {
    return null
  }

  return (
    <>
      <div
        className={cx(Styles.container, Styles.open, {
          [Styles.closed]: !templatesStore.isPickerVisible,
          [Styles.withRightSidebar]: !!rootStore.docStore?.rightRailStore.locked && editorState,
          [Styles.withLeftSidebar]: handbook && editorState,
        })}
      >
        <div className={Styles.heading}>
          <div ref={ref} className={Styles.categoryPicker}>
            <span>Start from</span>
            <MenuTrigger
              placement="top-start"
              onOpenChange={() => setMenuOpen(!menuOpen)}
              popoverClassName={Styles.popover}
            >
              <Button
                variant="tertiaryGreen"
                endIcon={menuOpen ? ChevronUp : ChevronDown}
                aria-label="Menu"
                className={Styles.menuTrigger}
                size={'md'}
              >
                {category?.name}
              </Button>
              <Menu
                selectionMode="single"
                onSelectionChange={(value) => {
                  if ((value as any).currentKey === undefined) return
                  templatesStore.setState({
                    selectedCategory: (value as any).currentKey,
                  })
                }}
                className={Styles.menu}
                selectedKeys={[selectedCategory as string]}
                itemClassName={Styles.item}
                showCheckmark={true}
              >
                {menuItems}
              </Menu>
            </MenuTrigger>
            <span>templates</span>
          </div>

          <div className={Styles.rightTopWrapper}>
            <Button
              variant="tertiaryGreen"
              size="md"
              onPress={() => {
                templatesStore.setState({
                  selectedCategory: category?.name || null,
                  isTemplateModalOpen: true,
                  isTemplateModalOpenFromPicker: true,
                })
              }}
            >
              View All
            </Button>
            <Button variant="tertiary" size="md" onPress={() => templatesStore.closePicker()} endIcon={CloseIcon} />
          </div>
        </div>
        <div>
          {categoriesQuery.isSuccess && (
            <DocCardSlider
              variant={'templatePickerSlider'}
              articles={category === undefined ? (categories || [])[0]?.articles || [] : category.articles}
              cardComponent={(article, className) => <TemplateMiniDocCard article={article} className={className} />}
            />
          )}
        </div>
      </div>
      <div></div>
    </>
  )
})
